import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Checkbox, Container, FormControlLabel, Grid, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { createProduct, getProduct, updateProduct, uploadProductImages } from '../services/Common/products.service';
import { listProductCategories } from '../services/Common/productCategories.service';
import { toast } from 'react-toastify';

const empty = { name: '', sku: '', description: '', categoryId: '', price: '', stock: '', status: 'active', images: [], variants: [], featured: false, popular: false, trooperExclusive: false, limitedEdition: false };

export default function ProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [variantText, setVariantText] = useState('[]');

  useEffect(() => {
    const load = async () => {
      if (!isEdit) return;
      try {
        const item = await getProduct(id);
        setForm({
          name: item.name || '', sku: item.sku || '', description: item.description || '',
          categoryId: item.categoryId || '', price: item.price || '', stock: item.stock || '', status: (item.status || 'active').toLowerCase(), images: item.images || [], variants: item.variants || [], featured: !!item.featured, popular: !!item.popular, trooperExclusive: !!item.trooperExclusive, limitedEdition: !!item.limitedEdition
        });
        setVariantText(JSON.stringify(item.variants || [], null, 2));
      } catch (e) { toast.error(e?.response?.data?.message || 'Failed to load product'); }
    };
    load();
  }, [id, isEdit]);

  useEffect(() => {
    const loadCats = async () => {
      try {
        const res = await listProductCategories({ page: 1, limit: 100, status: 'active', sort: 'name' });
        const items = res.items || res.results || res || [];
        setCategories(items);
      } catch (e) {
        console.error(e);
      }
    };
    loadCats();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      let variants;
      try { variants = JSON.parse(variantText || '[]'); } catch (_) { toast.error('Variants must be valid JSON'); return; }
      if (!Array.isArray(variants)) { toast.error('Variants must be a JSON array'); return; }
      const payload = { ...form, variants, price: Number(form.price), stock: Number(form.stock) };
      if (isEdit) await updateProduct(id, payload); else await createProduct(payload);
      toast.success(isEdit ? 'Product updated' : 'Product created');
      navigate('/products');
    } catch (e) { toast.error(e?.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const onUploadImages = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    try {
      setUploading(true);
      const res = await uploadProductImages(Array.from(files));
      const urls = res.urls || res || [];
      setForm(f => ({ ...f, images: [...(f.images || []), ...urls] }));
      toast.success(`Uploaded ${urls.length} image(s)`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  return (
    <>
      <Helmet><title>{isEdit ? 'Edit' : 'Create'} Product | Trooper</title></Helmet>
      <Container>
        <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
          <Typography variant='h4'>{isEdit ? 'Edit' : 'Create'} Product</Typography>
        </Stack>
        <Box component='form' onSubmit={onSubmit} sx={{ maxWidth: 720 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField fullWidth label='Name' value={form.name} onChange={e=>setForm(f=>({...f, name:e.target.value}))} required/></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label='SKU' value={form.sku} onChange={e=>setForm(f=>({...f, sku:e.target.value}))} required/></Grid>
            <Grid item xs={12}><TextField fullWidth label='Description' multiline minRows={3} value={form.description} onChange={e=>setForm(f=>({...f, description:e.target.value}))}/></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={3} label='Variants (JSON array)' placeholder='[{"size":"M","color":"Black"}]' value={variantText} onChange={e=>setVariantText(e.target.value)}/></Grid>
            <Grid item xs={12} md={6}>
              <Select fullWidth displayEmpty value={form.categoryId} onChange={e=>setForm(f=>({...f, categoryId:e.target.value}))} required>
                <MenuItem value='' disabled>Select Category</MenuItem>
                {categories.map(c => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={6}><FormControlLabel control={<Checkbox checked={form.featured} onChange={e=>setForm(f=>({...f, featured:e.target.checked}))}/>} label='Featured product'/></Grid>
            <Grid item xs={12} md={6}><FormControlLabel control={<Checkbox checked={form.popular} onChange={e=>setForm(f=>({...f, popular:e.target.checked}))}/>} label='Popular product'/></Grid>
            <Grid item xs={12} md={6}><FormControlLabel control={<Checkbox checked={form.trooperExclusive} onChange={e=>setForm(f=>({...f, trooperExclusive:e.target.checked}))}/>} label='Trooper Exclusive'/></Grid>
            <Grid item xs={12} md={6}><FormControlLabel control={<Checkbox checked={form.limitedEdition} onChange={e=>setForm(f=>({...f, limitedEdition:e.target.checked}))}/>} label='Limited Edition'/></Grid>
            <Grid item xs={6} md={3}><TextField type='number' fullWidth label='Price' value={form.price} onChange={e=>setForm(f=>({...f, price:e.target.value}))} required/></Grid>
            <Grid item xs={6} md={3}><TextField type='number' fullWidth label='Stock' value={form.stock} onChange={e=>setForm(f=>({...f, stock:e.target.value}))} required/></Grid>
            <Grid item xs={12} md={6}>
              <Select fullWidth value={form.status} onChange={e=>setForm(f=>({...f, status:e.target.value}))}>
                <MenuItem value='active'>Active</MenuItem>
                <MenuItem value='inactive'>Inactive</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Button variant='outlined' component='label' disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Upload Images'}
                  <input type='file' accept='image/*' hidden multiple onChange={onUploadImages} />
                </Button>
                <TextField fullWidth label='Current Image URLs' value={(form.images || []).join(', ')} onChange={e=>setForm(f=>({...f, images:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)}))} />
              </Stack>
            </Grid>
          </Grid>
          <Stack direction='row' spacing={2} mt={3}>
            <Button type='submit' variant='contained' disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
            <Button variant='outlined' onClick={()=>navigate('/products')}>Cancel</Button>
          </Stack>
        </Box>
      </Container>
    </>
  );
}
